import React from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  Button,
  Text,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import AttachmentGallery from './AttachmentGallery';

export const PHOTO_KIND = 'order_photo';

// Customer-supplied purchase order documents (usually a PDF). Kept as its own
// kind — not a photo category — so it lives in a dedicated section and can be
// individually attached to customer emails (mirrors tonnage_receipt). Must match
// the backend `purchase-order-att-<id>` email resolution.
export const PURCHASE_ORDER_KIND = 'purchase_order';

// Must match TonnageSection's RECEIPT_KIND — the landfill scale tickets captured
// per weighed line. Surfaced here read-only so all of an order's attachments
// live in one place; uploads still happen inline per line (where the line_id is
// known), so this section has no upload affordance.
const TONNAGE_RECEIPT_KIND = 'tonnage_receipt';

// Starting category set for order photos. Editable per-photo after upload.
export const PHOTO_CATEGORIES = ['Site', 'Delivery', 'Damage', 'Other'];

interface PhotoManagerDialogProps {
  /** TWM order UUID — same row the sidebar gallery attaches to. */
  orderId: string | null;
  open: boolean;
  onClose: () => void;
}

const useStyles = makeStyles({
  // Wide surface so the gallery finally has room — the whole point of moving
  // attachment management out of the 300px sidebar.
  surface: {
    width: 'min(960px, 92vw)',
    maxWidth: '960px',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  titleText: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
  },
  content: {
    // Cap the body height so a big gallery scrolls inside the dialog rather than
    // pushing it past the viewport.
    maxHeight: '70vh',
    ...shorthands.overflow('auto'),
  },
  // Divider + heading for the tonnage-receipts section below the photos grid.
  section: {
    marginTop: '20px',
    paddingTop: '16px',
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
  },
  sectionTitle: {
    display: 'block',
    marginBottom: '10px',
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
});

/**
 * Full-screen manager for an order's photos/attachments. Re-hosts the shared
 * AttachmentGallery in a wide dialog with a roomy thumbnail grid — the sidebar
 * keeps only a compact summary that opens this. No new data path: same query
 * key, endpoints, and storage as the sidebar gallery, so edits here are live in
 * the summary the moment the dialog closes.
 */
const PhotoManagerDialog: React.FC<PhotoManagerDialogProps> = ({ orderId, open, onClose }) => {
  const styles = useStyles();

  return (
    <Dialog open={open} onOpenChange={(_, data) => { if (!data.open) onClose(); }}>
      <DialogSurface className={styles.surface}>
        <DialogBody>
          <DialogTitle>
            <div className={styles.titleRow}>
              <span className={styles.titleText}>Photos &amp; Attachments</span>
              <Button
                appearance="subtle"
                icon={<Dismiss24Regular />}
                aria-label="Close"
                onClick={onClose}
              />
            </div>
          </DialogTitle>
          <DialogContent className={styles.content}>
            <AttachmentGallery
              orderId={orderId}
              kind={PHOTO_KIND}
              noun="photo"
              categories={PHOTO_CATEGORIES}
              thumbSize={120}
              acceptPdf
              previewMode="embedded"
              emptyHint="No photos or files yet — add site, delivery, or damage photos, or PDF documents here."
            />

            <div className={styles.section}>
              <Text className={styles.sectionTitle}>Purchase orders</Text>
              <AttachmentGallery
                orderId={orderId}
                kind={PURCHASE_ORDER_KIND}
                noun="purchase order"
                uploadLabel="Add PO"
                acceptPdf
                previewMode="embedded"
                thumbSize={120}
                emptyHint="Attach a customer purchase order (PDF or image). POs can be included in customer emails from the Send dialog."
              />
            </div>

            <div className={styles.section}>
              <Text className={styles.sectionTitle}>Tonnage receipts</Text>
              <AttachmentGallery
                orderId={orderId}
                kind={TONNAGE_RECEIPT_KIND}
                noun="receipt"
                acceptPdf
                disableUpload
                previewMode="embedded"
                thumbSize={120}
                emptyHint="Scale tickets uploaded per weighed line appear here."
              />
            </div>
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default PhotoManagerDialog;
